import config from '../config';
import { getToken, notifySessionExpired } from './auth';

const PUBLIC_ENDPOINTS = new Set([
  'login',
  'copyClient',
  'getUsdInrRate',
  'getDefaultScrollText',
  'getTodayScrollText',
  'logAutomation',
]);

function isSessionExpiredMessage(message) {
  return typeof message === 'string' && /session expired/i.test(message);
}

function throwSessionExpired(message) {
  notifySessionExpired(message);
  const error = new Error(message || 'Session expired. Please login again.');
  error.sessionExpired = true;
  throw error;
}

function wrapNetworkError(networkErr, url, kind) {
  if (networkErr && networkErr.sessionExpired) {
    throw networkErr;
  }
  const err = new Error(
    kind === 'mediator'
      ? `Unable to reach mediator. Often CORS or wrong API ORIGIN.`
      : `Unable to reach API. Often CORS (app origin not allowed) or wrong API ORIGIN.`
  );
  err.cause = networkErr;
  err.url = url;
  err.isNetworkError = true;
  throw err;
}

async function readJsonSafe(response) {
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch (e) {
      data = {};
    }
  }

  const replayHeaders = {};
  const contentType = response.headers && response.headers.get
    ? response.headers.get('content-type')
    : '';
  if (contentType) {
    replayHeaders['Content-Type'] = contentType;
  }

  return {
    data: data,
    replay: new Response(text, {
      status: response.status,
      statusText: response.statusText,
      headers: replayHeaders,
    }),
  };
}

async function interceptSessionExpired(response, endpoint) {
  if (PUBLIC_ENDPOINTS.has(endpoint)) {
    return response;
  }

  const parsed = await readJsonSafe(response);
  const message = parsed.data && parsed.data.message ? String(parsed.data.message) : '';
  if (isSessionExpiredMessage(message)) {
    throwSessionExpired(message);
  }

  return parsed.replay;
}

function buildFetchOptions(options, token) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return {
    ...options,
    method: options.method || 'POST',
    cache: 'no-store',
    headers,
  };
}

export async function checkAppSession() {
  try {
    const response = await apiAppFetch('getUserDetails', {
      method: 'POST',
      body: JSON.stringify({}),
    });

    if (response.status === 401) {
      let message = 'Session expired. Please login again.';
      try {
        const data = await response.json();
        if (data && data.message) {
          message = String(data.message);
        }
      } catch (e) {
        // use default message
      }
      throwSessionExpired(message);
    }

    return true;
  } catch (error) {
    if (error && error.sessionExpired) {
      return false;
    }
    return true;
  }
}

/**
 * Fetch against the secured React app API (api_app.php).
 * Attaches Bearer token when available.
 */
export async function apiAppFetch(endpoint, options = {}) {
  const token = await getToken();
  const url = `${config.API_APP_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, buildFetchOptions(options, token));
    return await interceptSessionExpired(response, endpoint);
  } catch (networkErr) {
    wrapNetworkError(networkErr, url, 'api');
  }
}

/**
 * Fetch against the secured React mediator (mediator_app.php).
 */
export async function mediatorAppFetch(options = {}) {
  const token = await getToken();
  const url = config.MEDIATOR_APP_URL;

  try {
    const response = await fetch(url, buildFetchOptions(options, token));
    return await interceptSessionExpired(response, 'mediator');
  } catch (networkErr) {
    wrapNetworkError(networkErr, url, 'mediator');
  }
}
